import { HiXMark, HiDevicePhoneMobile, HiArrowUpOnSquare } from 'react-icons/hi2';

interface InstallPromptProps {
  stationName?: string;
  stationImage?: string;
  isIOS?: boolean;
  onInstall: () => void;
  onDismiss: () => void;
}

export function InstallPrompt({
  stationName,
  stationImage,
  isIOS = false,
  onInstall,
  onDismiss,
}: InstallPromptProps) {
  const appName = stationName || 'AutoPod';

  return (
    <div className="fixed top-0 left-0 right-0 z-50 animate-slide-down
                    p-4 sm:left-auto sm:right-0 sm:max-w-sm"
         style={{ paddingTop: 'calc(1rem + var(--safe-area-inset-top, 0px))' }}>
      <div className="bg-[var(--color-surface-elevated)] rounded-xl
                      border border-[var(--color-border)]
                      shadow-xl shadow-black/30
                      overflow-hidden">
        {/* Content */}
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Icon - use station image if available */}
            {stationImage ? (
              <img
                src={stationImage}
                alt={appName}
                className="flex-shrink-0 w-10 h-10 rounded-lg object-cover"
              />
            ) : (
              <div className="flex-shrink-0 w-10 h-10 rounded-lg
                            bg-[var(--color-primary)]/20
                            flex items-center justify-center">
                <HiDevicePhoneMobile size={20} className="text-[var(--color-primary)]" />
              </div>
            )}

            {/* Text */}
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-[var(--color-text)]">
                Install {appName}
              </h3>
              <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                {isIOS
                  ? 'Add to your home screen for the best experience'
                  : 'Add to your home screen for quick access and offline listening'}
              </p>
            </div>

            {/* Close button */}
            <button
              onClick={onDismiss}
              className="flex-shrink-0 p-1 rounded-full
                       text-[var(--color-text-muted)]
                       hover:text-[var(--color-text)]
                       hover:bg-[var(--color-surface)]/60
                       transition-colors"
              aria-label="Dismiss"
            >
              <HiXMark size={18} />
            </button>
          </div>

          {/* iOS Instructions */}
          {isIOS ? (
            <div className="mt-3 p-3 rounded-lg bg-[var(--color-surface)]/60 border border-[var(--color-border)]/50">
              <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
                <span className="font-medium">1.</span>
                <span>Tap the</span>
                <HiArrowUpOnSquare size={16} className="text-[var(--color-primary)]" />
                <span>Share button below</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] mt-2">
                <span className="font-medium">2.</span>
                <span>Scroll down and tap</span>
                <span className="font-semibold text-[var(--color-text)]">"Add to Home Screen"</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] mt-2">
                <span className="font-medium">3.</span>
                <span>Tap</span>
                <span className="font-semibold text-[var(--color-text)]">"Add"</span>
                <span>to install</span>
              </div>
            </div>
          ) : (
            /* Android/Chrome Actions */
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={onDismiss}
                className="flex-1 px-3 py-2 text-xs font-medium
                         text-[var(--color-text-secondary)]
                         hover:text-[var(--color-text)]
                         hover:bg-[var(--color-surface)]/60
                         rounded-lg transition-colors"
              >
                Not now
              </button>
              <button
                onClick={onInstall}
                className="flex-1 px-3 py-2 text-xs font-semibold
                         text-white bg-[var(--color-primary)]
                         hover:bg-[var(--color-primary)]/90
                         rounded-lg transition-colors"
              >
                Install
              </button>
            </div>
          )}

          {/* iOS dismiss button */}
          {isIOS && (
            <button
              onClick={onDismiss}
              className="w-full mt-3 px-3 py-2 text-xs font-medium
                       text-[var(--color-text-secondary)]
                       hover:text-[var(--color-text)]
                       hover:bg-[var(--color-surface)]/60
                       rounded-lg transition-colors text-center"
            >
              Maybe later
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
